// hooks/useJobBoardView.tsx - Hook actualizado para lazy loading

import { useState, useEffect } from 'react';
import { 
  VacantePublica, 
  DatosPostulacion, 
  RespuestaServidor,
  VacanteDetalle,
  EstadoVacantes,
  ResponseConteos
} from '../types';
import { 
  jobBoardViewService
} from '../services/jobBoardViewService';

// Nueva interfaz para vacantes agrupadas (ahora con VacanteDetalle)
interface VacantesAgrupadasResumen {
  activas: VacanteDetalle[];
  inactivas: VacanteDetalle[];
}

// Hook principal
export function useJobBoardView() {
  // Estado para los resúmenes de vacantes (desde window)
  const [vacantesAgrupadas, setVacantesAgrupadas] = useState<VacantesAgrupadasResumen>({
    activas: [],
    inactivas: []
  });

  // Estado para los conteos administrativos
  const [detalleVacantes, setDetalleVacantes] = useState<VacanteDetalle[]>([]);
  const [totalesPorEstado, setTotalesPorEstado] = useState<EstadoVacantes[]>([]);

  // Estados de carga y error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos desde window.JobBoardViewData (síncronos y rápidos)
  const loadDataFromWindow = () => {
    try {
      // Cargar resúmenes de vacantes
      const vacantesData = jobBoardViewService.getVacantesPublicas();
      setVacantesAgrupadas(vacantesData);

      // Cargar conteos
      const conteosData = jobBoardViewService.getConteoVacantes();
      setDetalleVacantes(conteosData.detalle);
      setTotalesPorEstado(conteosData.totales_por_estado);

      setError(null);
    } catch (err: any) {
      console.error('Error al cargar datos desde window:', err);
      setError(err.message || 'Error al cargar los datos');
    }
  };

  // Refrescar datos desde el servidor (solo cuando sea necesario)
  const refreshDataFromServer = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await jobBoardViewService.refreshVacantesData();
      // Después de refrescar, cargar los nuevos datos desde window
      loadDataFromWindow();
    } catch (err: any) {
      console.error('Error al refrescar datos del servidor:', err);
      setError(err.message || 'Error al actualizar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Enviar postulación - delegado al servicio
  const enviarPostulacion = async (datos: DatosPostulacion): Promise<RespuestaServidor> => {
    try {
      const result = await jobBoardViewService.enviarPostulacion(datos);
      
      // Recargar datos después de enviar postulación exitosa
      if (result.success) {
        await refreshDataFromServer();
      }
      
      return result;
    } catch (err: any) {
      console.error('Error al enviar postulación:', err);
      return {
        success: false,
        message: err.message || 'Error al procesar la postulación'
      };
    }
  };

  // Obtener detalles completos de una vacante (lazy loading)
  const obtenerDetallesVacante = async (vacanteId: string): Promise<VacantePublica> => {
    try {
      return await jobBoardViewService.getVacanteCompleta(vacanteId);
    } catch (err: any) {
      console.error('Error al obtener detalles de vacante:', err);
      throw new Error(err.message || 'Error al cargar los detalles de la vacante');
    }
  };

  // Cargar datos al montar el componente (desde window, muy rápido)
  useEffect(() => {
    loadDataFromWindow();
  }, []);

  // Funciones auxiliares para obtener datos específicos
  const getActivasCount = (): number => {
    return totalesPorEstado.find(t => t.status_name === 'EN PROCESO')?.total;
  };

const getCerradasCount = (): number => {
  return totalesPorEstado
    .filter(t => t.status_name === 'CERRADA' || t.status_name === 'DESIERTA')
    .reduce((acc, t) => acc + Number(t.total || 0), 0);
};


  const getTotalPostulaciones = (): number => {
    return detalleVacantes.reduce((total, vacante) => total + vacante.total_postulaciones, 0);
  };

  // Función para obtener resumen de vacante por ID
  const getVacanteResumenById = (jobBoardId: string): VacanteDetalle | null => {
    const todasLasVacantes = [...vacantesAgrupadas.activas, ...vacantesAgrupadas.inactivas];
    return todasLasVacantes.find(v => v.job_board_id === jobBoardId) || null;
  };

  return {
    // Datos de resúmenes de vacantes (desde window)
    vacantesAgrupadas,
    vacantesActivas: vacantesAgrupadas.activas,
    vacantesInactivas: vacantesAgrupadas.inactivas,
    
    // Datos de conteos administrativos
    detalleVacantes,
    totalesPorEstado,
    
    // Estados de carga
    loading,
    
    // Estados de error
    error,
    hasError: error !== null,
    
    // Funciones
    refetch: refreshDataFromServer, // Refresca desde servidor
    reloadFromWindow: loadDataFromWindow, // Recarga desde window (rápido)
    enviarPostulacion,
    obtenerDetallesVacante, // 🚀 Nueva función para lazy loading
    
    // Funciones auxiliares
    getActivasCount,
    getCerradasCount,
    getTotalPostulaciones,
    getVacanteResumenById,
    
    // Utilidades de formateo (delegadas al servicio)
    formatearFecha: jobBoardViewService.formatearFecha,
    formatearSalario: jobBoardViewService.formatearSalario,
    calcularFaseActual: jobBoardViewService.calcularFaseActual
  };
}

// Hook específico solo para resúmenes de vacantes públicas (más ligero)
export function useVacantesPublicas() {
  const [vacantes, setVacantes] = useState<VacantesAgrupadasResumen>({
    activas: [],
    inactivas: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVacantes = () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = jobBoardViewService.getVacantesPublicas();
      setVacantes(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar vacantes');
    } finally {
      setLoading(false);
    }
  };

  const refreshVacantes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await jobBoardViewService.refreshVacantesData();
      loadVacantes(); // Recargar desde window después del refresh
    } catch (err: any) {
      setError(err.message || 'Error al actualizar vacantes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVacantes();
  }, []);

  return {
    vacantes,
    vacantesActivas: vacantes.activas,
    vacantesInactivas: vacantes.inactivas,
    loading,
    error,
    refetch: refreshVacantes
  };
}

// Hook específico solo para conteos administrativos
export function useVacantesConteos() {
  const [detalle, setDetalle] = useState<VacanteDetalle[]>([]);
  const [totales, setTotales] = useState<EstadoVacantes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConteos = () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = jobBoardViewService.getConteoVacantes();
      setDetalle(result.detalle);
      setTotales(result.totales_por_estado);
    } catch (err: any) {
      setError(err.message || 'Error al cargar conteos');
    } finally {
      setLoading(false);
    }
  };

  const refreshConteos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await jobBoardViewService.refreshVacantesData();
      loadConteos(); // Recargar desde window después del refresh
    } catch (err: any) {
      setError(err.message || 'Error al actualizar conteos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConteos();
  }, []);

  return {
    detalle,
    totales,
    loading,
    error,
    refetch: refreshConteos,
    getActivasCount: () => totales.find(t => t.status_name === 'EN PROCESO')?.total_vacantes || 0,
    getCerradasCount: () => totales.find(t => t.status_name === 'CERRADA')?.total_vacantes || 0
  };
}